/**
 * Tipos para o sistema de ações pós-disparo de campanhas
 * 
 * Permite configurar ações automáticas após cada disparo:
 * - add_tag: Adicionar tag ao contato
 * - assign_phase: Definir fase do Kanban
 * - assign_department: Atribuir departamento
 * - assign_agent: Atribuir agente
 * - assign_global_queue: Distribuir automaticamente via fila global
 * 
 * @author Sistema de Campanhas
 */

import { LucideIcon, Tag, Columns3, Building2, UserCheck, CheckCircle2, CircleCheck, CircleX, ListOrdered, Workflow } from 'lucide-react';

// Tipos de ação disponíveis
export type CampaignActionType = 
  | 'add_tag'             // Adicionar tag ao contato
  | 'assign_phase'        // Definir fase do Kanban
  | 'assign_department'   // Atribuir departamento
  | 'assign_agent'        // Atribuir agente
  | 'assign_global_queue' // Distribuir automaticamente via fila global
  | 'start_flow';         // Disparar um fluxo de automação (Chamar Fluxo)


// Condições de execução
export type ExecutionCondition = 
  | 'all'     // Executar para todos os disparos
  | 'success' // Executar apenas se disparo foi sucesso
  | 'error';  // Executar apenas se disparo falhou

// Interface para item de ação de campanha
export interface CampaignActionItem {
  id: string;
  action_type: CampaignActionType;
  action_value: Record<string, string>; // { tag_id, phase_id, department_id, agent_id }
  execution_condition: ExecutionCondition;
  sort_order: number;
}

// Interface para registro no banco de dados
export interface CampaignActionRecord {
  id?: string;
  campaign_id: string;
  action_type: CampaignActionType;
  action_value: Record<string, string>;
  execution_condition: ExecutionCondition;
  sort_order: number;
  created_at?: string;
}

// Configuração visual dos tipos de ação com ícones Lucide
export const campaignActionConfig: Record<CampaignActionType, {
  label: string;
  icon: LucideIcon;
  iconColor: string;      // Cor do ícone
  bgColor: string;        // Cor de fundo do container do ícone
  description: string;
  valueKey: string;       // Chave do valor no action_value
}> = {
  add_tag: {
    label: 'Adicionar Tag',
    icon: Tag,
    iconColor: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    description: 'Adiciona uma tag ao contato',
    valueKey: 'tag_id',
  },
  assign_phase: {
    label: 'Definir Fase Kanban',
    icon: Columns3,
    iconColor: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-100 dark:bg-violet-900/30',
    description: 'Move a conversa para uma fase do Kanban',
    valueKey: 'phase_id',
  },
  assign_department: {
    label: 'Atribuir Departamento',
    icon: Building2,
    iconColor: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    description: 'Encaminha a conversa para um departamento',
    valueKey: 'department_id',
  },
  assign_agent: {
    label: 'Atribuir Agente',
    icon: UserCheck,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    description: 'Atribui um agente específico à conversa',
    valueKey: 'agent_id',
  },
  assign_global_queue: {
    label: 'Fila de Distribuição',
    icon: ListOrdered,
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    description: 'Distribui automaticamente para um agente da fila',
    valueKey: 'queue_id',
  },
  start_flow: {
    label: 'Chamar Fluxo',
    icon: Workflow,
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
    description: 'Dispara um fluxo de automação após o envio da campanha',
    valueKey: 'flow_id',
  },
};


// Configuração das condições de execução com ícones Lucide
export const executionConditionConfig: Record<ExecutionCondition, {
  label: string;
  icon: LucideIcon;
  color: string;
  description: string;
}> = {
  all: {
    label: 'Todos',
    icon: CheckCircle2,
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    description: 'Executa para todos os disparos',
  },
  success: {
    label: 'Sucesso',
    icon: CircleCheck,
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    description: 'Apenas quando o disparo foi enviado com sucesso',
  },
  error: {
    label: 'Erro',
    icon: CircleX,
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    description: 'Apenas quando o disparo falhou',
  },
};
